import { tryCatch } from 'ramda';
import { Editor as SlateEditor, Element, Transforms, Path, TextUnit } from 'slate';
import { RenderElementProps } from 'slate-react';
import { v4 as uuidv4 } from 'uuid';
import { CustomEditor } from 'slate/types/slate';
import { hasText } from 'slate/functions';
import { Paragraph } from 'slate/components/Paragraph';
import { isParagraph, isCustomFormatting, defaultParagraph } from './utils/paragraphUtils';

const paragraph =
  (options: { withComments?: boolean; withStyledParagrah?: boolean; withTags?: boolean } = {}) =>
  (editor: CustomEditor) => {
    const withComments = options.withComments ?? false;
    const withStyledParagrah = options.withStyledParagrah ?? false;
    const withTags = options.withTags ?? false;
    const { insertBreak, normalizeNode, renderElement, deleteBackward } = editor;

    editor.deleteBackward = (unit: TextUnit) => {
      if (!editor.selection) {
        deleteBackward(unit);
        return;
      }

      // Default behaviour if in middle of text
      if (editor.selection.focus.offset !== 0) {
        deleteBackward(unit);
        return;
      }

      const currentParagraph = SlateEditor.above(editor, {
        at: editor.selection.anchor.path,
        match: (n) => isParagraph(n) || isCustomFormatting(n),
      });
      if (!currentParagraph) {
        deleteBackward(unit);
        return;
      }

      const before = SlateEditor.before(editor, editor.selection.anchor.path);
      if (!before) {
        return;
      }

      deleteBackward(unit);
    };

    editor.normalizeNode = (entry) => {
      const [node, path] = entry;

      // Only deal with paragraphs without uuid
      if (!isParagraph(node) && !isCustomFormatting(node)) {
        normalizeNode(entry);
        return;
      }
      if (!node.uuid) {
        Transforms.setNodes(
          editor,
          {
            uuid: uuidv4(),
          },
          {
            at: path,
          }
        );
        return;
      }

      if (path[path.length - 1] !== 0 && !hasText(node)) {
        const prevPath = path.slice();
        prevPath[prevPath.length - 1]--;
        const prevNode = SlateEditor.node(editor, prevPath)[0];
        if (!hasText(prevNode) && !(Element.isElement(prevNode) && editor.isVoid(prevNode))) {
          Transforms.removeNodes(editor, {
            at: path,
            match: (_node) => _node === node,
          });
          return;
        }

        const nextPath = path.slice();
        nextPath[nextPath.length - 1]++;
        const nextNode = tryCatch(
          () => SlateEditor.node(editor, nextPath)?.[0],
          () => undefined
        )();
        if (nextNode && (isParagraph(nextNode) || isCustomFormatting(nextNode)) && !hasText(nextNode)) {
          Transforms.removeNodes(editor, {
            at: nextPath,
            match: (_node) => _node === nextNode,
          });
          return;
        }
      }
      // If no modifications were made, pass on the torch
      normalizeNode(entry);
    };

    editor.insertBreak = () => {
      if (!editor.selection) {
        return insertBreak();
      }
      const styledParagraphEntry = SlateEditor.above(editor, {
        match: (n) => Element.isElement(n) && n.type === 'styled-paragraph',
      });
      if (styledParagraphEntry) {
        return insertBreak();
      }
      const match = SlateEditor.above(editor, {
        match: (n) => isParagraph(n) || isCustomFormatting(n),
      });
      if (!!match) {
        const [, blockPath] = match;
        const text = SlateEditor.string(editor, blockPath);
        if (text === '') {
          SlateEditor.withoutNormalizing(editor, () => {
            const newPara = { ...defaultParagraph(uuidv4()), justInsterted: true };
            Transforms.insertNodes(editor, newPara, { at: Path.next(blockPath) });

            Transforms.select(editor, {
              anchor: { path: [...Path.next(blockPath), 0], offset: 0 },
              focus: { path: [...Path.next(blockPath), 0], offset: 0 },
            });
          });
          return;
        }
      }
      insertBreak();
    };

    editor.renderElement = (props: RenderElementProps & { withComments?: boolean; withTags?: boolean }) => {
      if (isParagraph(props.element) || isCustomFormatting(props.element)) {
        return <Paragraph {...props} withComments={withComments} withStyledParagrah={withStyledParagrah} withTags={withTags} />;
      } else {
        props.withComments = withComments;
        props.withTags = withTags;
        return renderElement(props);
      }
    };

    return editor;
  };

export default paragraph;
